import {
  CHART_LINEAR_TIMESTAMP_FORMAT,
  formatRegularHour,
  getDateWithTimeZone,
  MONTH_DAY
} from '@/solaris-lens-common-lib/src/utils/dates';

const resolveChartTimezone = (timezone?: string): string =>
  timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Converts a UTC timestamp with the same helper used by Last Update and the
 * calendar END date, then formats it for chart display.
 * @param {string} timestamp - UTC timestamp from history series.
 * @param {string} format - dayjs format string.
 * @param {string} [timezone] - Turbine IANA timezone from the endpoint.
 * @returns {string} Formatted timestamp in the resolved timezone.
 */
export const formatChartTimestamp = (
  timestamp: string,
  format: string,
  timezone?: string
): string => {
  if (!timestamp) return '';

  const tz = resolveChartTimezone(timezone);
  try {
    const zonedDate = getDateWithTimeZone({ date: timestamp, timezone: tz });
    if (zonedDate.isValid()) return zonedDate.format(format);
  } catch {
    // Invalid IANA timezone falls back to the browser timezone below.
  }

  const fallbackDate = getDateWithTimeZone({
    date: timestamp,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
  return fallbackDate.isValid() ? fallbackDate.format(format) : '';
};

export const formatTickDate = (timestamp: string, timezone?: string) =>
  formatChartTimestamp(timestamp, MONTH_DAY, timezone);

export const formatTickTime = (timestamp: string, timezone?: string) =>
  formatChartTimestamp(timestamp, formatRegularHour, timezone);

export const formatChartLinearTimestamp = (
  timestamp: string,
  timezone?: string
) => formatChartTimestamp(timestamp, CHART_LINEAR_TIMESTAMP_FORMAT, timezone);
